<template>
    <div class="area container-fluid">
        <div class="row mt-2">
            <div class="col-6">
                <div v-show="!loading" class="header mb-2">Camera Image</div>
                <img v-show="!loading" :src="cameraImage" class="img-fluid" @load.prevent="cameraImageLoaded" @error="cameraImageLoaded" alt="Camera">
            </div>
            <div class="col-6 carinfo">
                <div class="header">Vehicle Information</div>
                <div class="row">
                    <div class="col-4 vehiclemake">Make</div>
                    <div class="col-8">
                        {{ vehicleMake }}
                    </div>
                    <div class="col-4">Model</div>
                    <div class="col-8 vehiclemodel">
                        {{ vehicleModel }}
                    </div>
                    <div class="col-4">Year</div>
                    <div class="col-8">
                        {{ vehicleYear }}
                    </div>
                </div>
            </div>
        </div>
        <loading class="mt-3" :loading="loading" text="Loading Camera Image..."/>
        <div v-show="!loading" class="buttonrow">
            <button-button type="button" class="btn btn-danger small me-2" @click.prevent="cancelClicked" :disabled="loading">Cancel</button-button>
            <button-button type="button" class="btn btn-primary me-2" @click.prevent="recalibrateClicked" :disabled="loading">Realign</button-button>
            <button-button type="button" class="btn btn-success me-2" @click.prevent="sendFirstImageClicked" :disabled="loading">Send Target</button-button>
        </div>
    </div>
</template>

<style scoped lang="scss">
    .header {
        font-size: 2rem;
        border-bottom: 1px solid white;
    }
    .carinfo {
        font-size: 2rem;
        hr {
            margin-top:0;
        }
    }
    .vehiclemake,
    .vehiclemodel {
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
    }
</style>

<script lang="ts">

import { defineComponent } from "vue";

import PageBase from "@/shared/components/common/PageBase";
import HeaderMessage from "@/components/HeaderMessage.vue";
import { SignalR } from "@/support/SignalR";
import { CalibrationEventSupport } from "@/support/CalibrationEvent";
import Utility from "@/shared/support/Utility";
import { Global } from "@/support/GlobalData";
import { DataRequest } from "@/shared/support/Data";
import { ButtonClicked } from "@/shared/enums/ButtonClicked";
import { confirmOk, confirmYesNo } from "@/shared/components/common/AlertDialog.vue";
import { CameraImageDto } from "@/shared/models/CameraImageDto";
import { DeviceStatusDto } from "@/shared/models/DeviceStatusDto";
import { CalibrationEvent } from "@/shared/enums/CalibrationEvent";

export default defineComponent({
    name: "CalibrationCameraImage",
    mixins: [PageBase],
    components: {
        HeaderMessage,
    },
    data() {
        return {
            cameraImageCache: "",
            vehicle: `${Global.Year} ${Global.Make} ${Global.Model}`,
            vehicleMake: Global.Make,
            vehicleModel: Global.Model,
            vehicleYear: Global.Year,
        };
    },
    computed: {
        cameraImage: {
            get(): string {
                return this.cameraImageCache;
            },
            set(image: string) {
                this.cameraImageCache = image;
            }
        }
    },
    methods: {
        loadCameraImage(): void {

            this.loading = true;

            if (Global.IsDemo()) {

                this.cameraImage = Global.DemoCameraImage;
                this.loading = false;

            } else {

                const request = new DataRequest();
                request.$post<any, CameraImageDto>("/Service/CameraParser/GetCameraImageInfo", { CalibrationSessionGuid: Global.CalibrationSession!.CalibrationSessionGuid })
                    .then((cameraImage: CameraImageDto): void => {
                        if (!cameraImage.Url) {
                            this.loading = false;
                            confirmOk("An unexpected error occurred retrieving the camera image");
                            this.$router.push("/Calibration/CameraSetup");
                            return;
                        }
                        const url = Utility.formatUrl(cameraImage.Url, {}, Global.GetWebAPIUrl(), true);
                        this.loading = true; // we'll get an onload event (cameraImageLoaded) which clears the loading indicator
                        this.cameraImage = url;
                    })
                    .catch((reason): void => {
                        this.loading = false;
                        const msg = `An unexpected error occurred retrieving the camera image - ${reason}`;
                        CalibrationEventSupport.sendEventDeviceFailure(msg);
                        confirmOk(msg);
                        this.$router.push("/Calibration/CameraSetup");
                    });

            }
        },

        cameraImageLoaded(): void {
            this.loading = false;

            CalibrationEventSupport.sendEventStatus({
                Page: "CalibrationCameraImage",
            } as DeviceStatusDto);
        },

        cancelClicked(): void {
            confirmYesNo("Are you sure you want to cancel this calibration?")
                .then((clicked: ButtonClicked): void => {
                    if (clicked === ButtonClicked.Ok) {
                        CalibrationEventSupport.sendEvent(CalibrationEvent.CalibrationCustomerCanceled);
                        Global.MainApp.clearSession();
                        this.$router.push("/");
                    }
                });
        },

        recalibrateClicked(): void {
            this.$router.push("/Calibration/CameraSetup");
        },

        sendFirstImageClicked(): void {
            this.$router.push("/Calibration/SendTargets");
        },

        getCurrentStatusForListeners(): void {
            if (this.loading) {
                CalibrationEventSupport.sendEventStatus({
                    Page: "CalibrationCameraReady",
                    CameraVerifying: true,
                } as DeviceStatusDto);
            } else {
                CalibrationEventSupport.sendEventStatus({
                    Page: "CalibrationCameraImage",
                } as DeviceStatusDto);
            }
        }
    },
    mounted(): void {
        SignalR.onGetCurrentStatusForListeners = this.getCurrentStatusForListeners;
        SignalR.onSendTargetByCompanion = this.sendFirstImageClicked;
        SignalR.onRestartCalibration = this.recalibrateClicked;
        this.loadCameraImage();
    },
    unmounted(): void {
        SignalR.onGetCurrentStatusForListeners = null;
        SignalR.onSendTargetByCompanion = null;
        SignalR.onRestartCalibration = null;
    }
});
</script>
